import * as _ from 'lodash-es';
import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import PageLayout from '../app/layout/PageLayout';
import TwoColumnRow from '../app/layout/TwoColumnRow';
import EmptyState from '../components/EmptyState';
import InfiniteScrollWaypoint from '../components/InfiniteScrollWaypoint';
import PageTitle from '../components/PageTitle';
import PostSortMenuButton from '../components/PostSortMenuButton';
import { gql } from '../graphql/generated/gql';
import { PostFilter, PostSort } from '../graphql/generated/graphql';
import PostRow from '../post/PostRow';
import PostRowMeta from '../post/PostRowMeta';
import PostSectionHeader from '../post/PostSectionHeader';
import PageMeta from '../utils/PageMeta';
import ConnectionHelper from '../utils/graphql/ConnectionHelper';
import * as styles from './UnorganizedView.module.scss';

const POST_SORT_OPTIONS = [
  PostSort.Popularity,
  PostSort.Edited,
  PostSort.Created,
  PostSort.Alphabetical,
] satisfies PostSort[];

const UNORGANIZED_POSTS_QUERY = gql(/** @complexity 4 */ `
  query UnorganizedView(
    $afterCursor: String
    $filters: [PostFilter!]
    $sortBy: PostSort
  ) {
    listPosts(filters: $filters, sortBy: $sortBy, first: 20, topicIds: [], after: $afterCursor) {
      edges {
        node {
          id
          views
          editedAt
          insertedAt
          ...PostRow_post
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`);

const METADATA_ATTRIBUTE = {
  [PostSort.Popularity]: 'popularity',
  [PostSort.Edited]: 'editedAt',
  [PostSort.Created]: 'insertedAt',
  [PostSort.Alphabetical]: 'editedAt',
} as const;

const UnorganizedView = () => {
  const [isArchivedIncluded, setIsArchivedIncluded] = useState(false);
  const [sortBy, setSortBy] = useState<(typeof POST_SORT_OPTIONS)[number]>(
    PostSort.Popularity,
  );

  const result = useQuery(UNORGANIZED_POSTS_QUERY, {
    variables: {
      filters: isArchivedIncluded
        ? [PostFilter.Published]
        : [PostFilter.Published, PostFilter.Unarchived],
      sortBy,
    },
  });

  const fetchMore = ConnectionHelper.generateFetchMore(
    UNORGANIZED_POSTS_QUERY,
    result,
    'listPosts',
  );

  const posts = _.compact(
    result.data?.listPosts?.edges?.map((edge) => edge?.node),
  );

  return (
    <PageLayout.Content noPaddingX noPaddingY>
      <PageMeta title="Unorganized Posts - Slab" />
      <PostRow.Provider>
        <div className={styles.container}>
          <TwoColumnRow
            main={
              <PageTitle
                title="Unorganized"
                subtitle="These posts have no topics. Add a topic to make these posts easier for teammates discover and read."
              />
            }
          />
          <TwoColumnRow
            full={
              <>
                <PostSectionHeader
                  title="Posts"
                  controls={
                    <PostSortMenuButton
                      allowedPostSorts={POST_SORT_OPTIONS}
                      sortBy={sortBy}
                      onSortByChange={setSortBy}
                      isArchivedIncluded={isArchivedIncluded}
                      onIsArchivedIncludedChange={setIsArchivedIncluded}
                    />
                  }
                />
                {posts.map((post) => (
                  <PostRow
                    key={post.id}
                    post={post}
                    meta={
                      <PostRowMeta
                        attribute={METADATA_ATTRIBUTE[sortBy]}
                        values={{
                          editedAt: post.editedAt,
                          insertedAt: post.insertedAt,
                          popularity: post.views,
                        }}
                      />
                    }
                  />
                ))}
                {posts.length === 0 && !result.loading ? (
                  <EmptyState
                    imageName="posts"
                    title="No posts here…"
                    message="All posts are organized"
                  />
                ) : (
                  <InfiniteScrollWaypoint fetchMore={fetchMore} />
                )}
              </>
            }
          />
        </div>
      </PostRow.Provider>
    </PageLayout.Content>
  );
};

export default UnorganizedView;
